/**
 * Feature Request API context.
 *
 * Lets a single wizard component (FeatureRequestSubmitPage) work for
 * both the authenticated admin flow and the public anonymous flow by
 * injecting:
 *   - `apiClient`  : axios instance (the admin one carries the JWT cookie;
 *                    the public one carries the per-draft `X-FR-Token`)
 *   - `pathPrefix` : "/feature-requests" (admin) or "/public/feature-requests"
 *   - `isPublic`   : true when the public flow is active
 *   - `setAccessToken(token)` : public-only, no-op for admin. Persists
 *                    the token so the wizard's polling + autosave calls
 *                    can re-attach it.
 *
 * The provider is intentionally lightweight — the bulk of the wizard
 * logic uses `frApi.get/post/patch/delete(<relative-path>, ...)`.
 */
import React, { createContext, useContext, useMemo, useRef } from "react";
import axios from "axios";
import { api as defaultAdminApi } from "@/lib/api";
import { getBackendUrl as resolveBackendUrl } from "@/lib/runtimeConfig";

const FrApiContext = createContext(null);

const BACKEND_URL = resolveBackendUrl();
const API = `${BACKEND_URL}/api`;


/**
 * Build a public axios instance pre-wired with the X-FR-Token header.
 * The token is stored in a ref so updates to it propagate to all
 * in-flight + future requests without re-creating the instance.
 */
function buildPublicClient(tokenRef) {
    const instance = axios.create({ baseURL: API });
    instance.interceptors.request.use((config) => {
        const t = tokenRef.current;
        if (t) {
            config.headers = config.headers || {};
            config.headers["X-FR-Token"] = t;
        }
        return config;
    });
    return instance;
}


export function FrApiProvider({ children, mode = "admin", initialToken = null }) {
    // ── Public-mode token ────────────────────────────────────
    const tokenRef = useRef(initialToken);
    const setAccessToken = (t) => {
        tokenRef.current = t || null;
        // Public submissions can resume across page reloads by stashing
        // the token in sessionStorage (intentionally NOT localStorage —
        // we don't want it to outlive the browser tab).
        if (mode === "public") {
            if (t) sessionStorage.setItem("frPublicToken", t);
            else sessionStorage.removeItem("frPublicToken");
        }
    };

    // Eager hydrate from sessionStorage on first mount if the parent
    // didn't pass an initialToken (covers full-page refresh of the
    // public submit page mid-wizard).
    React.useEffect(() => {
        if (mode === "public" && !tokenRef.current) {
            const stashed = sessionStorage.getItem("frPublicToken");
            if (stashed) tokenRef.current = stashed;
        }
    }, [mode]);

    const value = useMemo(() => {
        if (mode === "public") {
            const client = buildPublicClient(tokenRef);
            const prefix = "/public/feature-requests";
            return {
                isPublic: true,
                pathPrefix: prefix,
                apiClient: client,
                setAccessToken,
                get:    (path, ...rest) => client.get(`${prefix}${path}`,    ...rest),
                post:   (path, ...rest) => client.post(`${prefix}${path}`,   ...rest),
                patch:  (path, ...rest) => client.patch(`${prefix}${path}`,  ...rest),
                delete: (path, ...rest) => client.delete(`${prefix}${path}`, ...rest),
            };
        }
        const prefix = "/feature-requests";
        return {
            isPublic: false,
            pathPrefix: prefix,
            apiClient: defaultAdminApi,
            setAccessToken,    // no-op for admin
            get:    (path, ...rest) => defaultAdminApi.get(`${prefix}${path}`,    ...rest),
            post:   (path, ...rest) => defaultAdminApi.post(`${prefix}${path}`,   ...rest),
            patch:  (path, ...rest) => defaultAdminApi.patch(`${prefix}${path}`,  ...rest),
            delete: (path, ...rest) => defaultAdminApi.delete(`${prefix}${path}`, ...rest),
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode]);

    return <FrApiContext.Provider value={value}>{children}</FrApiContext.Provider>;
}


export function useFrApi() {
    const ctx = useContext(FrApiContext);
    if (!ctx) {
        throw new Error("useFrApi must be used inside <FrApiProvider>");
    }
    return ctx;
}
